import { db } from "@/firebase/init";
import { doc, collection, getDocs, setDoc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";

/**
 * Get user's personal timetable entries
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, entries: Array, message: string}>}
 */
export const getPersonalTimetable = async (userId) => {
  try {
    const entriesRef = collection(db, `users/${userId}/personalTimetable`);
    const entriesSnapshot = await getDocs(entriesRef);
    
    const entries = [];
    entriesSnapshot.forEach((doc) => {
      entries.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return {
      success: true,
      entries
    };
  } catch (error) {
    console.error("Error getting personal timetable:", error);
    return {
      success: false,
      entries: [],
      message: error.message
    };
  }
};

/**
 * Add a new entry to the user's personal timetable
 * @param {string} userId - The user ID
 * @param {Object} entryData - The entry data to add
 * @returns {Promise<{success: boolean, id: string, message: string}>}
 */
export async function addPersonalTimetableEntry(userId, entryData) {
  if (!userId || !entryData) {
    console.error("Missing userId or entryData in addPersonalTimetableEntry", { userId, entryData });
    return { success: false, message: 'Missing required data' };
  }

  try {
    // Validate entry data has all required fields
    const requiredFields = ['title', 'date', 'startTime', 'endTime', 'type'];
    for (const field of requiredFields) {
      if (!entryData[field]) {
        return { 
          success: false, 
          message: `Missing required field: ${field}` 
        };
      }
    }
    
    // Create a clean copy of the entry data
    const sanitizedData = {
      title: entryData.title,
      date: entryData.date,
      startTime: entryData.startTime,
      endTime: entryData.endTime,
      type: entryData.type, // e.g., 'event', 'task', 'reminder', 'study-session'
      color: entryData.color || '#3B82F6',
      location: entryData.location || '',
      description: entryData.description || '',
      isCompleted: entryData.isCompleted || false,
      reminder: entryData.reminder || null
    };
    
    // Create a document reference for the new entry
    const entryDocRef = doc(collection(db, `users/${userId}/personalTimetable`));
    
    // Add the document
    await setDoc(entryDocRef, {
      ...sanitizedData,
      id: entryDocRef.id,
      createdAt: serverTimestamp()
    });

    return {
      success: true,
      id: entryDocRef.id,
      message: 'Entry added successfully'
    };
  } catch (error) {
    console.error('Error adding personal timetable entry:', error);
    return {
      success: false,
      message: error.message || 'Failed to add entry'
    };
  }
}

/**
 * Update an existing entry in the user's personal timetable
 * @param {string} userId - The user ID
 * @param {string} entryId - The entry document ID to update
 * @param {Object} entryData - The updated entry data
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const updatePersonalTimetableEntry = async (userId, entryId, entryData) => {
  try {
    const entryRef = doc(db, `users/${userId}/personalTimetable`, entryId);
    
    // Remove the id from the data to avoid storing it in the document
    const { ...dataToUpdate } = entryData;
    // id is destructured but not used, so just use dataToUpdate

    await updateDoc(entryRef, {
      ...dataToUpdate,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true
    };
  } catch (error) {
    console.error("Error updating personal timetable entry:", error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Delete an entry from the user's personal timetable
 * @param {string} userId - The user ID
 * @param {string} entryId - The entry document ID to delete
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const deletePersonalTimetableEntry = async (userId, entryId) => {
  try {
    const entryRef = doc(db, `users/${userId}/personalTimetable`, entryId);
    await deleteDoc(entryRef);
    
    return {
      success: true
    };
  } catch (error) {
    console.error("Error deleting personal timetable entry:", error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Toggle completion status of a personal timetable entry
 * @param {string} userId - The user ID
 * @param {string} entryId - The entry document ID
 * @param {boolean} isCompleted - The new completion status
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const togglePersonalTimetableCompletion = async (userId, entryId, isCompleted) => {
  try {
    const entryRef = doc(db, `users/${userId}/personalTimetable`, entryId);
    await updateDoc(entryRef, {
      isCompleted,
      completedAt: isCompleted ? serverTimestamp() : null
    });
    
    return {
      success: true
    };
  } catch (error) {
    console.error("Error toggling completion:", error);
    return {
      success: false,
      message: error.message
    };
  }
};
